import { useEffect } from 'react';
import { services } from '../../mocks/services';

export default function ServicesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://atlastransportlogistic.fr';

  useEffect(() => {
    document.title = 'Nos Services - Transport Aérien Maritime Afrique | ATLAS Logistics Nanterre';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', "Découvrez nos services de transport aérien et maritime vers l'Afrique : fret international, groupage marchandises, dédouanement, stockage, logistique import-export depuis Nanterre Paris.");

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}/services`;

    const schemaService = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Transport et Logistique Internationale",
      "provider": {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#organization`,
        "name": "ATLAS Transport & Logistics",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Nanterre",
          "addressRegion": "Île-de-France",
          "addressCountry": "FR"
        }
      },
      "areaServed": {
        "@type": "Country",
        "name": ["France", "Guinée", "Côte d'Ivoire", "Sénégal", "Togo", "Ghana", "Mali"]
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Services de Transport et Logistique",
        "itemListElement": services.map((service) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service.title,
            "description": service.description
          }
        }))
      }
    };

    const schemaBreadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": siteUrl },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": `${siteUrl}/services` }
      ]
    };

    const schemas = [schemaService, schemaBreadcrumb];
    const scriptIds = ['ld-services-page', 'ld-breadcrumb-services'];

    schemas.forEach((schema, i) => {
      let script = document.getElementById(scriptIds[i]) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptIds[i];
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      scriptIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white py-24">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/0a33daefc60cde9913273f5cf84df28b.jpg')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold mb-6 font-['Playfair_Display']">
                Nos <strong>Services de Transport</strong>
              </h1>
              <p className="text-xl text-slate-200 leading-relaxed">
                Solutions complètes de transport aérien et maritime, groupage de marchandises, logistique internationale et dédouanement pour vos expéditions vers l'Afrique
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <article key={index} className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 group">
                  <div className="h-56 overflow-hidden bg-slate-100">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-lg mb-4">
                      <i className={`${service.icon} text-2xl text-teal-600`}></i>
                    </div>
                    <h2 className="text-2xl font-bold mb-3 text-slate-900 font-['Playfair_Display']">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-slate-700">
                          <i className="ri-check-line text-teal-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6 font-['Playfair_Display']">
              Besoin d'un devis personnalisé ?
            </h2>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour obtenir une solution de transport adaptée à vos besoins
            </p>
            <a
              href="/#contact"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Demander un devis gratuit
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
